import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useStore } from 'context/store-context';
import { useUser } from 'context/user-context';
import { isShipaidAdmin } from 'utils';
import { canManageRequests } from 'types/resolution';
import { NonNegativeNumberInput } from './NonNegativeNumberInput';
import {
  GrantReturnExceptionInput,
  ReturnException,
  ReturnExceptionItem,
  ReturnExceptionOrder,
  ReturnExceptionOverride,
  returnExceptionOverrideLabels,
} from 'types/returnExceptions';
import {
  GET_RETURN_EXCEPTION_ORDER,
  GRANT_RETURN_EXCEPTION,
  REVOKE_RETURN_EXCEPTION,
} from 'gql/returnExceptions';

const MAX_EXPIRY = 30 * 24 * 60 * 60 * 1000;
const buttonClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const inputClass =
  'rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50';
const knownOverride = (code: string): code is ReturnExceptionOverride =>
  Object.prototype.hasOwnProperty.call(returnExceptionOverrideLabels, code);
const selectable = (item: ReturnExceptionItem) =>
  !item.unavailable &&
  item.availableQuantity > 0 &&
  item.blockers.length > 0 &&
  item.blockers.every(knownOverride);
const itemTitle = (item: ReturnExceptionItem) =>
  item.title?.trim() || 'Untitled item';
const validOrder = (
  value: any,
  storeId: string,
  orderId: string,
): value is ReturnExceptionOrder =>
  value?.orderId === orderId &&
  Array.isArray(value.items) &&
  Array.isArray(value.exceptions) &&
  value.items.every(
    (item: any) =>
      typeof item?.orderItemId === 'string' &&
      (item.title === null || typeof item.title === 'string') &&
      Number.isInteger(item.availableQuantity) &&
      item.availableQuantity >= 0 &&
      Array.isArray(item.blockers) &&
      item.blockers.every((code: unknown) => typeof code === 'string') &&
      typeof item.unavailable === 'boolean',
  ) &&
  value.exceptions.every(
    (exception: any) =>
      exception?.orderId === orderId &&
      exception.storeId === storeId &&
      typeof exception.id === 'string' &&
      Array.isArray(exception.items) &&
      exception.items.every(
        (item: any) =>
          typeof item?.orderItemId === 'string' &&
          Number.isInteger(item.quantity) &&
          item.quantity > 0,
      ) &&
      Array.isArray(exception.overrideCodes) &&
      exception.overrideCodes.every(
        (code: unknown) => typeof code === 'string',
      ) &&
      typeof exception.reason === 'string' &&
      typeof exception.stale === 'boolean' &&
      Number.isFinite(Date.parse(exception.expiresAt)),
  );

function status(exception: ReturnException, now: number) {
  if (exception.consumedAt) return 'Used';
  if (exception.revokedAt) return 'Revoked';
  if (exception.stale) return 'Needs renewal';
  if (Date.parse(exception.expiresAt) <= now) return 'Expired';
  return 'Ready to use';
}

function ExceptionControls({
  storeId,
  orderId,
}: {
  storeId: string;
  orderId: string;
}) {
  const { data, loading, error, refetch } = useQuery<{
    returnExceptionOrder: ReturnExceptionOrder;
  }>(GET_RETURN_EXCEPTION_ORDER, {
    variables: { storeId, orderId },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [grant] = useMutation<{ grantReturnException: ReturnExceptionOrder }>(
    GRANT_RETURN_EXCEPTION,
  );
  const [revoke] = useMutation<{ revokeReturnException: ReturnExceptionOrder }>(
    REVOKE_RETURN_EXCEPTION,
  );
  const [updated, setUpdated] = useState<ReturnExceptionOrder | null>(null);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [overrides, setOverrides] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState('');
  const [now, setNow] = useState(Date.now);
  const mounted = useRef(true);
  const pending = useRef(false);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  useEffect(() => {
    setUpdated(null);
  }, [data]);
  const order = updated ?? data?.returnExceptionOrder;
  const renderedAt = Math.max(now, Date.now());
  const nextExpiry = validOrder(order, storeId, orderId)
    ? Math.min(
        ...order.exceptions
          .filter((exception) => !exception.revokedAt && !exception.consumedAt)
          .map((exception) => Date.parse(exception.expiresAt))
          .filter((time) => time > renderedAt),
      )
    : Infinity;
  useEffect(() => {
    if (!Number.isFinite(nextExpiry)) return;
    // A deadline crossed since rendering still needs one immediate refresh.
    const delay = Math.max(0, nextExpiry - Math.max(now, Date.now()));
    const timer = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(delay, 2147483647),
    );
    return () => window.clearTimeout(timer);
  }, [nextExpiry, now]);

  if (loading)
    return (
      <p role="status" className="p-4 text-sm text-gray-500">
        Loading return exceptions…
      </p>
    );
  if (error || !validOrder(order, storeId, orderId))
    return (
      <div role="alert" className="p-4 text-sm text-red-700">
        Return exceptions could not be loaded for this order.
        <button
          type="button"
          className="ml-2 underline"
          onClick={() => {
            void refetch().catch(() => undefined);
          }}
        >
          Retry loading exceptions
        </button>
      </div>
    );

  const selected = order.items.filter((item) =>
    Object.prototype.hasOwnProperty.call(quantities, item.orderItemId),
  );
  const requiredOverrides = Array.from(
    new Set(selected.flatMap((item) => item.blockers)),
  );
  const expires = Date.parse(expiresAt);
  const valid =
    selected.length > 0 &&
    selected.every(
      (item) =>
        selectable(item) &&
        Number.isInteger(Number(quantities[item.orderItemId])) &&
        Number(quantities[item.orderItemId]) > 0 &&
        Number(quantities[item.orderItemId]) <= item.availableQuantity,
    ) &&
    requiredOverrides.every(
      (code) => knownOverride(code) && overrides.includes(code),
    ) &&
    reason.trim().length > 0 &&
    reason.trim().length <= 500 &&
    Number.isFinite(expires) &&
    expires > Date.now() &&
    expires <= Date.now() + MAX_EXPIRY;
  const existing = order.exceptions.find(
    (exception) => !exception.revokedAt && !exception.consumedAt,
  );

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || existing || pending.current) return;
    pending.current = true;
    setBusy('grant');
    setFailure('');
    const input: GrantReturnExceptionInput = {
      storeId,
      orderId,
      items: selected.map((item) => ({
        orderItemId: item.orderItemId,
        quantity: Number(quantities[item.orderItemId]),
      })),
      overrideCodes: requiredOverrides.filter(knownOverride),
      reason: reason.trim(),
      expiresAt: new Date(expires).toISOString(),
    };
    try {
      const result = await grant({ variables: { input } });
      if (!mounted.current) return;
      const currentOrder = result.data?.grantReturnException;
      if (!validOrder(currentOrder, storeId, orderId))
        throw new Error('Invalid response');
      setUpdated(currentOrder);
      setQuantities({});
      setOverrides([]);
      setReason('');
      setExpiresAt('');
    } catch {
      if (mounted.current)
        setFailure(
          'The exception could not be granted. Review the current order and try again.',
        );
    } finally {
      if (mounted.current) {
        pending.current = false;
        setBusy(null);
      }
    }
  }

  async function revokeException(exceptionId: string) {
    if (pending.current) return;
    pending.current = true;
    setBusy(exceptionId);
    setFailure('');
    try {
      const result = await revoke({
        variables: { storeId, orderId, exceptionId },
      });
      if (!mounted.current) return;
      const currentOrder = result.data?.revokeReturnException;
      if (!validOrder(currentOrder, storeId, orderId))
        throw new Error('Invalid response');
      setUpdated(currentOrder);
    } catch {
      if (mounted.current)
        setFailure(
          'The exception could not be revoked. Refresh the order and try again.',
        );
    } finally {
      if (mounted.current) {
        pending.current = false;
        setBusy(null);
      }
    }
  }

  return (
    <div className="space-y-4 p-4">
      <p className="text-sm text-gray-600">
        Let the customer submit one return for selected items and quantities.
        Choose exactly which policy checks to override.
      </p>
      {failure && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 p-3 text-sm text-red-700"
        >
          {failure}
        </p>
      )}
      {order.exceptions.length > 0 && (
        <ul aria-label="Return exception history" className="space-y-3">
          {order.exceptions.map((exception) => (
            <li
              key={exception.id}
              className="rounded-lg border border-gray-200 p-3 text-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong>{status(exception, renderedAt)}</strong>
                {!exception.revokedAt && !exception.consumedAt && (
                  <button
                    type="button"
                    disabled={Boolean(busy)}
                    className={buttonClass}
                    onClick={() => {
                      void revokeException(exception.id);
                    }}
                  >
                    {busy === exception.id ? 'Revoking…' : 'Revoke exception'}
                  </button>
                )}
              </div>
              <p className="mt-2 text-gray-700">
                {exception.items
                  .map(
                    (item) =>
                      `${item.quantity} × ${
                        order.items.find(
                          (candidate) =>
                            candidate.orderItemId === item.orderItemId,
                        )?.title || 'Item'
                      }`,
                  )
                  .join(', ')}
              </p>
              <p className="mt-1 text-gray-600">
                Overrides:{' '}
                {exception.overrideCodes
                  .map((code) =>
                    knownOverride(code)
                      ? returnExceptionOverrideLabels[code]
                      : 'policy restriction',
                  )
                  .join(', ')}
                .
              </p>
              <p className="mt-1 break-words text-gray-600">
                Internal reason: {exception.reason}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Expires {new Date(exception.expiresAt).toLocaleString()} (your
                local time).
              </p>
              {exception.stale &&
                !exception.revokedAt &&
                !exception.consumedAt && (
                  <p className="mt-2 text-amber-800">
                    The order or policy changed. Revoke this exception and
                    review the current items before granting another.
                  </p>
                )}
            </li>
          ))}
        </ul>
      )}
      {existing ? (
        <p className="text-sm text-gray-600">
          Revoke the unused exception before granting a replacement. A grant can
          be used only once.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <fieldset disabled={Boolean(busy)} className="space-y-3">
            <legend className="mb-2 text-sm font-semibold">
              Items covered by this exception
            </legend>
            {order.items.length === 0 && (
              <p className="text-sm text-gray-500">
                There are no items available for an exception.
              </p>
            )}
            {order.items.map((item) => (
              <div
                key={item.orderItemId}
                className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-gray-200 p-3"
              >
                <label className="flex min-w-0 flex-1 gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    aria-label={`Select ${itemTitle(item)}`}
                    checked={Object.prototype.hasOwnProperty.call(
                      quantities,
                      item.orderItemId,
                    )}
                    disabled={!selectable(item) || Boolean(busy)}
                    onChange={(event) => {
                      const next = { ...quantities };
                      if (event.target.checked) next[item.orderItemId] = '1';
                      else delete next[item.orderItemId];
                      setQuantities(next);
                      setFailure('');
                    }}
                  />
                  <span className="min-w-0 break-words">
                    <span className="font-medium">{itemTitle(item)}</span>
                    <span className="mt-1 block text-xs text-gray-500">
                      {item.unavailable ||
                      item.blockers.some((code) => !knownOverride(code))
                        ? 'Eligibility cannot be verified. This item cannot be overridden.'
                        : item.availableQuantity === 0
                        ? 'No quantity available to return.'
                        : item.blockers.length === 0
                        ? 'No policy exception needed.'
                        : item.blockers
                            .map((code) =>
                              knownOverride(code)
                                ? returnExceptionOverrideLabels[code]
                                : 'policy restriction',
                            )
                            .join(', ')}
                    </span>
                  </span>
                </label>
                {Object.prototype.hasOwnProperty.call(
                  quantities,
                  item.orderItemId,
                ) && (
                  <label className="text-xs text-gray-600">
                    Quantity (up to {item.availableQuantity})
                    <NonNegativeNumberInput
                      aria-label={`Quantity for ${itemTitle(item)}`}
                      min={1}
                      max={item.availableQuantity}
                      step={1}
                      value={quantities[item.orderItemId]}
                      onChange={(event) =>
                        setQuantities({
                          ...quantities,
                          [item.orderItemId]: event.target.value,
                        })
                      }
                      className={`${inputClass} ml-2 w-20`}
                    />
                  </label>
                )}
              </div>
            ))}
          </fieldset>
          {requiredOverrides.length > 0 && (
            <fieldset
              disabled={Boolean(busy)}
              className="space-y-2 rounded-lg bg-amber-50 p-3"
            >
              <legend className="text-sm font-semibold">
                Policy checks to override
              </legend>
              {requiredOverrides.filter(knownOverride).map((code) => (
                <label key={code} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={overrides.includes(code)}
                    onChange={(event) =>
                      setOverrides(
                        event.target.checked
                          ? [...overrides, code]
                          : overrides.filter((value) => value !== code),
                      )
                    }
                  />
                  Override {returnExceptionOverrideLabels[code]}
                </label>
              ))}
              <p className="text-xs text-gray-600">
                Select every listed check for the chosen items. Other policy
                requirements still apply.
              </p>
            </fieldset>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium">
              Internal reason
              <textarea
                aria-label="Internal reason"
                value={reason}
                maxLength={500}
                disabled={Boolean(busy)}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                className={`${inputClass} mt-1 block w-full`}
              />
              <span className="mt-1 block text-xs font-normal text-gray-500">
                Visible to your team only.
              </span>
            </label>
            <label className="text-sm font-medium">
              Expires at (your local time)
              <input
                aria-label="Expires at (your local time)"
                type="datetime-local"
                value={expiresAt}
                disabled={Boolean(busy)}
                onChange={(event) => setExpiresAt(event.target.value)}
                className={`${inputClass} mt-1 block w-full`}
              />
              <span className="mt-1 block text-xs font-normal text-gray-500">
                Choose a future date within 30 days.
              </span>
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Only the selected items and quantities are covered, for one return.
            The exception does not change fees or return methods.
          </p>
          <button
            type="submit"
            disabled={!valid || Boolean(busy)}
            className={`${buttonClass} border-blue-700 text-blue-800`}
          >
            {busy === 'grant'
              ? 'Granting exception…'
              : 'Grant one-return exception'}
          </button>
        </form>
      )}
    </div>
  );
}

export function OrderReturnExceptionPanel({ orderId }: { orderId: string }) {
  const { storeId } = useStore();
  const { role, user } = useUser();
  if (!storeId || !orderId || !canManageRequests(role, isShipaidAdmin(user)))
    return null;
  return (
    <section
      aria-labelledby="order-return-exception-title"
      className="mb-6 rounded-lg border border-gray-200 bg-white"
    >
      <h2
        id="order-return-exception-title"
        className="border-b border-gray-100 px-4 py-3 text-base font-semibold"
      >
        One-return exception
      </h2>
      <ExceptionControls
        key={`${storeId}:${orderId}`}
        storeId={storeId}
        orderId={orderId}
      />
    </section>
  );
}
